/**
 * Per-category kind flags that drive conditional form logic.
 *
 * Form code must branch on these flags rather than on literal category IDs
 * so that tenants who customize their categories still get the right form.
 */
export type MileageOrHours = "mileage" | "hours" | null;

export interface CategoryKind {
  /** True for categories whose items normally have a title (e.g. vehicles). Auto-checks the Titled Item flag in the form. */
  is_titled?: boolean;
  /** True when items in this category use a 17-character NHTSA-format VIN. Drives VIN-vs-Serial input field and VIN auto-lookup. Independent of `is_titled` (a boat may be titled but use a HIN, not a VIN). */
  uses_vin?: boolean;
  /** True for categories representing real estate / land parcels. Shows acreage + per-acre pricing, relaxes seller requirement. */
  is_land?: boolean;
  /** Whether equipment fields display mileage, hours, or neither. */
  uses_mileage_or_hours?: MileageOrHours;
  /** True for categories that show the make/model/year/serial equipment block. */
  equipment_form?: boolean;
}

export interface Category extends CategoryKind {
  id: string;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const DEFAULT_CATEGORIES: Category[] = [
  {
    id: "cat-land",
    name: "Land",
    description: "Real estate and land parcels",
    sortOrder: 0,
    isActive: true,
    is_land: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "cat-construction",
    name: "Construction Equipment",
    description: "Heavy machinery and construction tools",
    sortOrder: 1,
    isActive: true,
    equipment_form: true,
    uses_mileage_or_hours: "hours",
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "cat-farm",
    name: "Farm Equipment",
    description: "Agricultural machinery and implements",
    sortOrder: 2,
    isActive: true,
    equipment_form: true,
    uses_mileage_or_hours: "hours",
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "cat-transport",
    name: "Transport",
    description: "Trucks, trailers, and transportation equipment",
    sortOrder: 3,
    isActive: true,
    is_titled: true,
    uses_vin: true,
    equipment_form: true,
    uses_mileage_or_hours: "mileage",
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "cat-other",
    name: "Other",
    description: "Miscellaneous items",
    sortOrder: 4,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
];

export function getCategoryById(categories: Category[], id: string): Category | undefined {
  return categories.find((category) => category.id === id);
}

export function getActiveCategories(categories: Category[]): Category[] {
  return categories
    .filter((category) => category.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function formatCategory(categories: Category[], categoryId?: string): string {
  if (!categoryId) {
    return "";
  }

  const category = getCategoryById(categories, categoryId);
  return category?.name ?? "";
}
